import {
  IFileChunkUploader,
  IFileChunk,
  IUploaderConfig,
  IUploadMetadata,
  IBlobReader,
  IThumbnaiProvider,
  IUploaderTicketProvider,
  IChunkSizeProvider,
} from "../config";
import { LocalStorageHelper } from "@travellocal/utils/localStorageHelper"

// Webpack 2 + isomorphic conflict
// https://github.com/github/fetch/issues/125#issuecomment-94266403
if (process.env.IS_BROWSER) {
  require("whatwg-fetch");
}

export interface IFileTransporter {
  upload(ticket: string, bytes: ArrayBuffer, fileChunk: IFileChunk, progress?: (percentage: number) => void): () => Promise<string>;
}

class UploaderTicketProvider implements IUploaderTicketProvider{
  constructor(private url: string){}
  getTicket(metadata: IUploadMetadata){
    const r: RequestInit = {
      method: "POST",
      body: JSON.stringify(metadata),
      headers: {
        "Authorization": "Bearer " + LocalStorageHelper.getItem("authtoken"),
        'Content-Type': "application/json",
      }
    }

    return window.fetch(this.url, r).then(resp => {
      if (!resp.ok) {
        throw "Unable to get upload ticket!"
      }
      return resp.json().then(j => {
        return j.ticket;
      }, err => {
        throw "Unable to parse upload ticket!"
      })
    }, err => {
        throw "Error getting upload ticket!"
      });
  }
}

export class XMLHttpRequestUploaderConfig implements IUploaderConfig {
  chunkUploader: IFileChunkUploader;
  constructor(private url: string, blobReader: IBlobReader, public thumbnaiProvider: IThumbnaiProvider, public chunkSizeProvider: IChunkSizeProvider) {
    this.chunkUploader = new XMLHttpRequestUploader(blobReader, new XMLHttpRequestTransporter(url));
  }

  ticketProvider: IUploaderTicketProvider = new UploaderTicketProvider(this.url);
}

class XMLHttpRequestTransporter implements IFileTransporter {
  private xhr: XMLHttpRequest;
  constructor(private url: string) { }
  upload(ticket: string, bytes: ArrayBuffer, fileChunk: IFileChunk, progress?: (percentage: number) => void) {
    return () => new Promise<string>((res, rej) => {

      var xmlHttpRequest = this.xhr = new XMLHttpRequest();
      xmlHttpRequest.open("PUT", this.url, true);
      this.setHeaders(xmlHttpRequest, fileChunk, ticket);

      xmlHttpRequest.upload.onabort = function(e) {
        rej('Upload cancelled');
      };

      if (progress) {
        xmlHttpRequest.upload.onprogress = (evt: ProgressEvent) => {
          if (evt.lengthComputable) {
            progress(Math.round((evt.loaded / evt.total) * 100));
          }
        };

        xmlHttpRequest.upload.onloadstart = (e) => {
          progress(0);
        };
        xmlHttpRequest.upload.onload = (e) => {
          progress(100);
        };
      }
      xmlHttpRequest.onreadystatechange = (evt: ProgressEvent) => {
        if (xmlHttpRequest.readyState < 4) {
          return;
        }

        if (xmlHttpRequest.status === 0) {
          rej("Network Error");
        } else if (xmlHttpRequest.status >= 400) {
          rej("Server Error");
        } else if (xmlHttpRequest.status === 202) {
          res(JSON.parse(xmlHttpRequest.responseText).fileId);
        }
        else {
          res(null);
        }
      }

      xmlHttpRequest.send(bytes);
    });
  }

  setHeaders(xmlHttpRequest: XMLHttpRequest, fileChunk: IFileChunk, ticket: string) {
    xmlHttpRequest.setRequestHeader("Content-Ticket", ticket);
    xmlHttpRequest.setRequestHeader("Content-Part-Number", fileChunk.info.number.toString());
    xmlHttpRequest.setRequestHeader("Authorization", "Bearer " + LocalStorageHelper.getItem("authtoken"));
    xmlHttpRequest.setRequestHeader("Content-Type", "application/octet-stream");// fileChunk.file.type);
    xmlHttpRequest.setRequestHeader("Content-Range", fileChunk.getContentRange());
  }
}

class XMLHttpRequestUploader implements IFileChunkUploader {
  constructor(private blober: IBlobReader, private transporter: IFileTransporter) { }
  upload(ticket: string, fileChunk: IFileChunk) {
    return new Promise<string>((res, rej) => {
      var blob = fileChunk.getBlob();
      this.uploadChunk(ticket, fileChunk, res, rej);
    })
  }


  private retry(promise: () => Promise<string>, maxCount = 3, count = 1): Promise<string> {
    return promise().catch(err => {
      if (count === maxCount) {
        throw err;
      }
      return this.retry(promise, maxCount, ++count);
    })
  }

  private uploadChunk(ticket: string, fileChunk: IFileChunk, ok: (value?: string) => void, fail: (error?: any) => void) {
    var blob = fileChunk.getBlob();
    this.blober.read(blob).then(arr => {
      this.retry(this.transporter.upload(ticket, arr, fileChunk, pct => { }))
        .then(v => ok(v))
        .catch(e => fail(e));
    }).catch(e => fail(e));
  }
}
